<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import API from 'dashboard/api/shopifyOrderActions';
import OrderActionSummary from './OrderActionSummary.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import OrderManagementOverview from './OrderManagementOverview.vue';
import OrderProductCatalog from './OrderProductCatalog.vue';
import OrderProductImage from './OrderProductImage.vue';

const props = defineProps({
  order: { type: Object, required: true },
  conversationId: { type: [Number, String], required: true },
});
const { t } = useI18n();
const manager = ref(null);
const step = ref('edit');
const busy = ref(false);
const error = ref('');
const context = ref(null);
const preview = ref(null);
const sent = ref(false);
const kind = ref('refund_percentage');
const value = ref('30');
const reason = ref('');
const notify = ref(true);
const restock = ref(true);
const refund = ref(true);
const quantities = ref({});
const quantityDrafts = ref({});
const hasUnappliedQuantities = computed(
  () =>
    kind.value === 'edit' &&
    Object.keys(quantities.value).some(
      id => Number(quantityDrafts.value[id]) !== quantities.value[id]
    )
);
const canAdjust = id => {
  const quantity = Number(quantityDrafts.value[id]);
  return (
    Number.isInteger(quantity) &&
    quantity >= 1 &&
    quantity <= 999 &&
    quantity !== quantities.value[id]
  );
};
const adjustQuantity = id => {
  if (!busy.value && canAdjust(id))
    quantities.value[id] = Number(quantityDrafts.value[id]);
};
const removeProduct = id => {
  quantities.value[id] = 0;
  quantityDrafts.value[id] = 0;
};
watch(
  quantityDrafts,
  () => {
    preview.value = null;
  },
  { deep: true }
);
const additions = ref([]);
const kinds = ['refund_percentage', 'refund_amount', 'refund_shipping'];
const params = computed(() => ({
  hook_id: props.order.hook_id,
  order_id: String(props.order.id),
  conversation_id: props.conversationId,
}));
const requiredScopes = computed(() =>
  kind.value === 'edit'
    ? ['write_order_edits', 'read_products']
    : ['write_orders']
);
const missing = computed(() =>
  requiredScopes.value.filter(scope => !context.value?.scopes.includes(scope))
);
const details = computed(() => ({
  value: value.value,
  reason: reason.value,
  notify: notify.value,
  restock: restock.value,
  refund: refund.value,
  changes: (context.value?.order.lineItems.nodes || [])
    .filter(line => Number(quantities.value[line.id]) !== line.currentQuantity)
    .map(line => ({ line_id: line.id, quantity: quantities.value[line.id] })),
  additions: additions.value.map(item => ({
    variant_id: item.id,
    quantity: item.quantity,
  })),
}));
watch(
  details,
  () => {
    preview.value = null;
    sent.value = false;
  },
  { deep: true }
);
watch(kind, () => {
  preview.value = null;
  sent.value = false;
});
const run = async operation => {
  busy.value = true;
  error.value = '';
  try {
    await operation();
  } catch (e) {
    error.value = e.response?.data?.error || t('ORDER_ACTIONS.ERROR');
  } finally {
    busy.value = false;
  }
};
const load = () =>
  run(async () => {
    context.value = null;
    preview.value = null;
    sent.value = false;
    step.value = 'edit';
    kind.value = 'refund_percentage';
    value.value = '30';
    reason.value = '';
    notify.value = true;
    restock.value = true;
    refund.value = true;
    additions.value = [];
    await nextTick();
    manager.value.open();
    const requested = params.value;
    const response = await API.context(requested);
    if (
      requested.conversation_id !== props.conversationId ||
      requested.order_id !== String(props.order.id)
    )
      return;
    context.value = response.data;
    quantities.value = Object.fromEntries(
      context.value.order.lineItems.nodes.map(line => [
        line.id,
        line.currentQuantity,
      ])
    );
    quantityDrafts.value = { ...quantities.value };
  });
const add = product => {
  const existing = additions.value.find(item => item.id === product.id);
  if (existing) existing.quantity += 1;
  else additions.value.push({ ...product, quantity: 1 });
};
const makePreview = () =>
  run(async () => {
    if (hasUnappliedQuantities.value) return;
    preview.value = (
      await API.create({
        ...params.value,
        kind: kind.value,
        details: details.value,
        request_key: crypto.randomUUID(),
      })
    ).data;
  });
const submit = () =>
  run(async () => {
    if (!preview.value || sent.value || step.value !== 'review') return;
    preview.value = (await API.submit(preview.value.id)).data;
    sent.value = true;
  });
watch(
  () => [props.conversationId, props.order.id, props.order.hook_id],
  () => {
    manager.value?.close();
    step.value = 'edit';
    notify.value = true;
    context.value = null;
    preview.value = null;
    sent.value = false;
  }
);
</script>

<template>
  <div v-if="order.hook_id" class="mb-4">
    <Button
      :label="t('ORDER_ACTIONS.OPEN')"
      variant="outline"
      size="sm"
      :disabled="busy"
      @click="load"
    />
    <Dialog
      ref="manager"
      :title="
        t(
          step === 'review'
            ? 'ORDER_ACTIONS.CONFIRM_TITLE'
            : 'ORDER_ACTIONS.OPEN'
        )
      "
      width="3xl"
      overflow-y-auto
      :show-confirm-button="false"
      :show-cancel-button="false"
    >
      <div
        class="flex min-w-0 flex-col gap-5 break-words text-sm leading-6 text-n-slate-12 [&_p]:mb-0 [&_h4]:mb-0 [&_label]:mb-0 [&_input]:!mb-0 [&_select]:!mb-0 [&_textarea]:!mb-0 [&_input]:!text-base [&_select]:!text-base [&_textarea]:!text-base [&_button]:min-h-11"
      >
        <p v-if="error" class="text-n-ruby-11" role="alert">{{ error }}</p>
        <p v-if="busy" role="status">{{ t('ORDER_ACTIONS.LOADING') }}</p>
        <template v-if="context && step === 'edit'">
          <OrderManagementOverview :order="context.order" />
          <div
            class="grid grid-cols-1 gap-2 sm:grid-cols-3 [&_button]:h-auto [&_button]:whitespace-normal [&_button]:py-3 [&_span]:whitespace-normal [&_span]:overflow-visible"
            :aria-label="t('ORDER_ACTIONS.KIND')"
          >
            <Button
              type="button"
              :label="t('ORDER_ACTIONS.REFUNDS')"
              :variant="kind.startsWith('refund') ? 'solid' : 'outline'"
              :aria-pressed="kind.startsWith('refund')"
              :disabled="busy"
              @click="kind = 'refund_percentage'"
            />
            <Button
              type="button"
              :label="t('ORDER_ACTIONS.EDIT')"
              :variant="kind === 'edit' ? 'solid' : 'outline'"
              :aria-pressed="kind === 'edit'"
              :disabled="busy"
              @click="kind = 'edit'"
            />
            <Button
              type="button"
              :label="t('ORDER_ACTIONS.CANCEL')"
              :variant="kind === 'cancel' ? 'solid' : 'outline'"
              :aria-pressed="kind === 'cancel'"
              :disabled="busy"
              @click="kind = 'cancel'"
            />
          </div>
          <p
            v-if="missing.length"
            class="rounded-lg bg-n-amber-2 p-3 text-n-amber-11"
          >
            {{ t('ORDER_ACTIONS.SCOPES') }} {{ missing.join(', ') }}
          </p>
          <section
            v-if="kind.startsWith('refund')"
            class="flex min-w-0 flex-col gap-3 rounded-xl border border-n-weak p-4"
          >
            <label
              >{{ t('ORDER_ACTIONS.REFUND_TYPE') }}
              <select v-model="kind" class="mt-1 min-h-11" :disabled="busy">
                <option v-for="item in kinds" :key="item" :value="item">
                  {{ t('ORDER_ACTIONS.' + item.toUpperCase()) }}
                </option>
              </select>
            </label>
            <p v-if="kind === 'refund_percentage'">
              {{ t('ORDER_ACTIONS.BASIS') }}
            </p>
            <label v-if="['refund_percentage', 'refund_amount'].includes(kind)"
              >{{ t('ORDER_ACTIONS.VALUE') }}
              <input
                v-model="value"
                class="mt-1 min-h-11"
                type="number"
                min="0"
                :max="kind === 'refund_percentage' ? 100 : undefined"
                step="any"
                :disabled="busy"
              />
            </label>
            <div
              v-if="kind === 'refund_percentage'"
              class="flex flex-wrap gap-2"
            >
              <Button
                v-for="percentage in [30, 50, 100]"
                :key="percentage"
                type="button"
                :label="`${percentage}%`"
                size="sm"
                variant="outline"
                :disabled="busy"
                @click="value = String(percentage)"
              />
            </div>
          </section>
          <section
            v-if="kind === 'edit'"
            class="flex min-w-0 flex-col gap-4 rounded-xl border border-n-weak p-4"
          >
            <h4 class="font-semibold">{{ t('ORDER_ACTIONS.PRODUCTS') }}</h4>
            <div
              v-for="line in context.order.lineItems.nodes"
              :key="line.id"
              class="flex min-w-0 flex-col gap-3 rounded-lg bg-n-alpha-2 p-3 sm:flex-row sm:items-end"
            >
              <div class="flex min-w-0 flex-1 items-start gap-3">
                <OrderProductImage :image="line.image" :title="line.name" />
                <div class="min-w-0 flex-1 break-words">
                  <strong class="block font-medium">{{ line.name }}</strong>
                  <p v-if="line.sku" class="text-xs text-n-slate-11">
                    {{ line.sku }}
                  </p>
                  <p>
                    {{ t('ORDER_ACTIONS.CURRENT_QUANTITY') }}:
                    {{ line.currentQuantity }}
                  </p>
                  <p
                    v-if="quantities[line.id] !== line.currentQuantity"
                    class="text-xs text-n-slate-11"
                    role="status"
                  >
                    {{
                      quantities[line.id] === 0
                        ? t('ORDER_ACTIONS.MARKED_FOR_REMOVAL')
                        : t('ORDER_ACTIONS.DRAFT_QUANTITY', {
                            quantity: quantities[line.id],
                          })
                    }}
                  </p>
                </div>
              </div>
              <div class="flex shrink-0 items-end gap-2">
                <label
                  class="flex w-16 shrink-0 flex-col gap-1 whitespace-nowrap"
                >
                  {{ t('ORDER_ACTIONS.QUANTITY') }}
                  <input
                    v-model="quantityDrafts[line.id]"
                    class="min-h-11 !w-full !px-2"
                    type="number"
                    min="1"
                    max="999"
                    step="1"
                    :disabled="busy"
                    @keydown.enter.prevent="adjustQuantity(line.id)"
                  />
                </label>
                <Button
                  type="button"
                  :label="t('ORDER_ACTIONS.ADJUST')"
                  size="sm"
                  variant="outline"
                  :disabled="busy || !canAdjust(line.id)"
                  @click="adjustQuantity(line.id)"
                />
                <Button
                  type="button"
                  :label="t('ORDER_ACTIONS.REMOVE')"
                  size="sm"
                  variant="outline"
                  color="ruby"
                  :disabled="busy || quantities[line.id] === 0"
                  @click="removeProduct(line.id)"
                />
              </div>
            </div>
            <OrderProductCatalog
              :params="params"
              :disabled="busy"
              :available="context.scopes.includes('read_products')"
              @add="add"
            />
            <strong v-if="additions.length">{{
              t('ORDER_ACTIONS.ADDITIONS')
            }}</strong>
            <div
              v-for="(item, index) in additions"
              :key="item.id"
              class="flex min-w-0 flex-col gap-2 rounded-lg bg-n-alpha-2 p-3 sm:flex-row sm:items-center"
            >
              <div class="flex min-w-0 flex-1 items-start gap-3">
                <OrderProductImage
                  :image="
                    item.media?.nodes?.[0]?.preview?.image ||
                    item.product.featuredMedia?.preview?.image
                  "
                  :title="item.product.title"
                />
                <div class="min-w-0 flex-1 break-words">
                  <strong class="block font-medium">{{
                    item.product.title
                  }}</strong>
                  <p v-if="item.title !== 'Default Title'">{{ item.title }}</p>
                  <p v-if="item.sku" class="text-xs text-n-slate-11">
                    {{ item.sku }}
                  </p>
                </div>
              </div>
              <label class="flex min-w-0 flex-col gap-1 sm:w-32"
                >{{ t('ORDER_ACTIONS.ADD_QUANTITY')
                }}<input
                  v-model="item.quantity"
                  type="number"
                  min="1"
                  max="999"
                  step="1"
                  :disabled="busy"
              /></label>
              <Button
                type="button"
                :label="t('ORDER_ACTIONS.REMOVE')"
                size="sm"
                variant="outline"
                :disabled="busy"
                @click="additions.splice(index, 1)"
              />
            </div>
            <p class="text-n-slate-11">{{ t('ORDER_ACTIONS.EDIT_NOTE') }}</p>
          </section>
          <section
            v-if="kind === 'cancel'"
            class="flex flex-col gap-3 rounded-xl border border-n-weak p-4"
          >
            <p class="text-n-ruby-11">{{ t('ORDER_ACTIONS.CANCEL_NOTE') }}</p>
            <label
              ><input
                v-model="refund"
                class="mt-1 shrink-0"
                type="checkbox"
                :disabled="busy"
              />
              {{ t('ORDER_ACTIONS.REFUND') }}</label
            >
          </section>
          <label
            v-if="['edit', 'cancel'].includes(kind)"
            class="flex min-h-11 items-start gap-3 rounded-lg border border-n-weak p-3"
            ><input
              v-model="restock"
              class="mt-1 shrink-0"
              type="checkbox"
              :disabled="busy"
            />
            {{ t('ORDER_ACTIONS.RESTOCK') }}</label
          >
          <label
            class="flex min-h-11 items-start gap-3 rounded-lg border border-n-weak p-3"
            ><input
              v-model="notify"
              class="mt-1 shrink-0"
              type="checkbox"
              :disabled="busy"
            />
            {{ t('ORDER_ACTIONS.NOTIFY') }}</label
          >
          <label
            >{{ t('ORDER_ACTIONS.REASON')
            }}<textarea
              v-model="reason"
              class="mt-1 min-h-28 resize-y"
              :placeholder="t('ORDER_ACTIONS.REASON_EXAMPLE')"
              maxlength="500"
              :disabled="busy"
            />
          </label>
          <p
            v-if="hasUnappliedQuantities"
            role="status"
            class="text-n-slate-11"
          >
            {{ t('ORDER_ACTIONS.ADJUST_HINT') }}
          </p>
          <Button
            type="button"
            :label="t('ORDER_ACTIONS.PREVIEW')"
            :disabled="
              busy ||
              missing.length > 0 ||
              !reason.trim() ||
              hasUnappliedQuantities
            "
            @click="makePreview"
          />
          <OrderActionSummary v-if="preview" :action="preview" />
          <Button
            v-if="preview"
            type="button"
            :label="
              t(
                context.mode === 'auto_allow'
                  ? 'ORDER_ACTIONS.EXECUTE'
                  : 'ORDER_ACTIONS.SUBMIT'
              )
            "
            :disabled="busy"
            @click="step = 'review'"
          />
        </template>
        <template v-if="context && preview && step === 'review'">
          <p v-if="!sent">
            {{
              t(
                context.mode === 'auto_allow'
                  ? 'ORDER_ACTIONS.CONFIRM_EXECUTES'
                  : 'ORDER_ACTIONS.CONFIRM_WAITS'
              )
            }}
          </p>
          <OrderActionSummary :action="preview" />
          <p v-if="sent" role="status">{{ t('ORDER_ACTIONS.SENT') }}</p>
        </template>
      </div>
      <template #description>
        <Button
          type="button"
          icon="i-lucide-x"
          variant="ghost"
          color="slate"
          class="absolute right-4 top-4"
          :aria-label="t('ORDER_ACTIONS.CLOSE')"
          :disabled="busy"
          @click="manager.close()"
        />
      </template>
      <template #footer>
        <div
          class="flex min-w-0 flex-col gap-3 sm:flex-row [&_button]:min-h-11"
        >
          <Button
            type="button"
            class="w-full"
            variant="faded"
            color="slate"
            :label="
              t(
                step === 'review' && !sent
                  ? 'ORDER_ACTIONS.GO_BACK'
                  : 'ORDER_ACTIONS.CLOSE'
              )
            "
            :disabled="busy"
            @click="
              step === 'review' && !sent ? (step = 'edit') : manager.close()
            "
          />
          <Button
            v-if="step === 'review' && !sent && preview"
            type="button"
            class="w-full"
            :label="
              t(
                context.mode === 'auto_allow'
                  ? 'ORDER_ACTIONS.CONFIRM_RUN'
                  : 'ORDER_ACTIONS.CONFIRM_SEND'
              )
            "
            :disabled="busy"
            @click="submit"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>
